import {
  calculateDiscountPercentageByDiscountAmountToFixed,
  toFixedNumber,
} from "../../base/utils";

// matches slugs like: about-these-items, ar-about-these-items,
// om-about-these-items, om-ar-about-these-items, sa-about-these-items, etc.
const ABOUT_SLUG_PATTERN = /about-these-items?/i;

// metadata keys sometimes come with stray whitespace from the CMS
// (e.g. "country_code " on sa-about-these-items) — normalize before matching
const getMeta = (metadata = [], key) =>
  metadata.find((item) => item?.key?.trim().toLowerCase() === key)?.value;

export const ProductTransformer = (product, lang = "EN", country = "") => {
  if (!product) return null;

  const display_gross_prices = product?.pricing?.displayGrossPrices || false;

  let start_undiscounted_price;
  let discount_amount;
  let start_pricing_range;
  let tax_amount;

  if (display_gross_prices) {
    start_undiscounted_price =
      toFixedNumber(
        product?.pricing?.priceRangeUndiscounted?.start?.gross?.amount
      ) || 0;
    discount_amount = product?.pricing?.discount?.gross?.amount || 0;
    start_pricing_range =
      toFixedNumber(product?.pricing?.priceRange?.start?.gross?.amount) || 0;
    tax_amount = 0;
  } else {
    start_undiscounted_price =
      toFixedNumber(
        product?.pricing?.priceRangeUndiscounted?.start?.net?.amount
      ) || 0;
    discount_amount = product?.pricing?.discount?.net?.amount || 0;
    start_pricing_range =
      toFixedNumber(product?.pricing?.priceRange?.start?.net?.amount) || 0;
    tax_amount =
      toFixedNumber(product?.pricing?.priceRange?.start?.tax?.amount) || 0;
  }

  let discount_percentage = 0;

  if (discount_amount) {
    discount_percentage = calculateDiscountPercentageByDiscountAmountToFixed(
      discount_amount,
      start_undiscounted_price
    );
  }

  const attributes = product?.attributes || [];

  const aboutAttributes = attributes.filter((attr) =>
    ABOUT_SLUG_PATTERN.test(attr?.attribute?.slug || "")
  );

  const normalizedLang = lang?.toUpperCase();
  const normalizedCountry = country?.toUpperCase();

  const metaOf = (attr, key) =>
    getMeta(attr?.attribute?.metadata, key)?.toUpperCase();

  // 1) exact country + lang match — highest priority regardless of values
  const countrySpecific = aboutAttributes.find(
    (attr) =>
      metaOf(attr, "country_code") === normalizedCountry &&
      metaOf(attr, "lang") === normalizedLang
  );

  // 2) lang-only generic (e.g. "ar-about-these-items"): has a lang but no
  // country_code — used when no country+lang match exists
  const langOnlyFallback = aboutAttributes.find(
    (attr) =>
      !getMeta(attr?.attribute?.metadata, "country_code") &&
      metaOf(attr, "lang") === normalizedLang
  );

  // 3) fully generic fallback: no country_code, no lang at all
  const genericFallback = aboutAttributes.find(
    (attr) =>
      !getMeta(attr?.attribute?.metadata, "country_code") &&
      !getMeta(attr?.attribute?.metadata, "lang")
  );

  const aboutAttributeRaw =
    countrySpecific || langOnlyFallback || genericFallback || null;
  const aboutTheseItems = aboutAttributeRaw
    ? ProductAttributesTransformer(aboutAttributeRaw)
    : null;

  return {
    id: product?.id,
    name: product?.name,
    image: product?.media?.length ? product?.media[0]?.url : "",
    payable_amount: start_pricing_range,
    price: start_undiscounted_price,
    tax: tax_amount,
    discount_percentage,
    discount_amount,
    currency: product?.pricing?.priceRange?.start?.gross?.currency,
    aboutTheseItems,
    details: attributes
      .filter(
        (attribute) => !ABOUT_SLUG_PATTERN.test(attribute?.attribute?.slug || "")
      )
      .map((attribute) => ProductAttributesTransformer(attribute)),
    variants: product?.variants || [],
    translation: product?.translation?.name,
    quantityAvailable:
      product?.variants && product?.variants[0].quantityAvailable,
  };
};

const ProductAttributesTransformer = (attribute) => {
  if (!attribute) return null;

  const metadata = attribute.attribute.metadata || [];
  const detailMetadata = metadata.find((item) => item.key === "detail");

  if (detailMetadata?.value === "false") {
    return {
      title: null,
      details: null,
    };
  }

  return {
    title: attribute?.attribute?.name,
    translation: attribute?.attribute?.translation?.name,
    details: attribute?.values.map(({ name, richText, inputType }) => ({
      name,
      richText,
      inputType,
    })),
  };
};